import { Toaster } from '@crowdfpl/ui-web/sonner';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { ThemeProvider, useTheme } from '@/components/theme-provider';
import { trackUmamiEvent } from '@/helpers/analytics';
import { captureSessionAttribution } from '@/helpers/attribution';
import { installDebugModeGlobal } from '@/hooks/utils/use-debug-mode';
import '@/services/faro';
import App from './App.tsx';
import './index.css';

// Check if analytics should be enabled
const isAnalyticsEnabled =
  import.meta.env.PROD ||
  import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

// Capture UTM/referrer immediately (before router can strip query params)
const attribution = captureSessionAttribution();

// Initialize Umami Analytics
if (isAnalyticsEnabled) {
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = 'https://umami.proto.by/script.js';
  script.setAttribute('data-website-id', '2b002022-1ad7-4eb8-8b59-92122aa95d56');
  script.addEventListener('load', () => {
    if (attribution?.source === 'email') {
      trackUmamiEvent('email_click_through', {
        campaign: attribution.utm.campaign ?? 'retention',
        term: attribution.utm.term,
      });
    }
  });
  document.head.appendChild(script);
  console.log('✅ Umami Analytics initialized');
} else {
  console.log('⚠️ Umami Analytics disabled (development mode)');
}

// Initialize Sentry (GlitchTip compatible)
// Only in production or when explicitly enabled in development
const shouldEnableGlitchtip =
  import.meta.env.VITE_PUBLIC_SENTRY_DSN && isAnalyticsEnabled;

if (shouldEnableGlitchtip) {
  Sentry.init({
    dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
    environment: import.meta.env.MODE || 'development',
    // Enable source maps for better error tracking
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Error tracking
    beforeSend(event, hint) {
      // In development, log to console
      if (import.meta.env.DEV) {
        console.error('Sentry event:', event, hint);
      }
      return event;
    },
  });
  console.log('✅ GlitchTip initialized');
} else {
  console.log('⚠️ GlitchTip disabled (development mode)');
}

installDebugModeGlobal();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppToaster() {
  const { resolvedTheme } = useTheme();
  return <Toaster resolvedTheme={resolvedTheme} />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <ThemeProvider defaultTheme="light" storageKey="crowdfpl-web-theme">
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <App />
            <AppToaster />
          </QueryClientProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
